import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { EntitySelect, EntitySelectEntity, Select } from '@factorialco/f0-react/dist/experimental'
import {
  AtsJobCatalogRoleSelectorFragment,
  AtsJobCatalogSelectorGetLevelsByRoleDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  selectedRoleUuid?: string
  selectedRoleLevelUuid?: string
  onLevelSelect: (roleUuid?: string, roleLevelUuid?: string) => void
  jobCatalogRoles: AtsJobCatalogRoleSelectorFragment[]
}

export const JobCatalogRoleSelector = ({
  selectedRoleUuid,
  selectedRoleLevelUuid,
  onLevelSelect,
  jobCatalogRoles,
}: Props) => {
  const [internalRoleUuid, setInternalRoleUuid] = useState<string | undefined>(selectedRoleUuid)
  const [internalLevelUuid, setInternalLevelUuid] = useState<string | undefined>(
    selectedRoleLevelUuid
  )
  const onLevelSelectRef = useRef(onLevelSelect)

  const { data: levelsData, loading: levelsLoading } = useQuery(
    AtsJobCatalogSelectorGetLevelsByRoleDocument,
    {
      variables: {
        roleUuid: internalRoleUuid ?? '',
      },
      skip: !internalRoleUuid,
    }
  )

  const levels = useMemo(() => levelsData?.jobCatalog.treeNodesConnection.nodes ?? [], [levelsData])

  const roleEntities = useMemo<EntitySelectEntity[]>(() => {
    const ancestorMap = new Map<
      number,
      { id: number; name: string; roles: AtsJobCatalogRoleSelectorFragment[] }
    >()

    jobCatalogRoles.forEach((role) => {
      const ancestorId = role.ancestor?.id
      if (ancestorId) {
        if (!ancestorMap.has(ancestorId)) {
          ancestorMap.set(ancestorId, {
            id: ancestorId,
            name: role.ancestor?.name ?? '',
            roles: [],
          })
        }
        const ancestorGroup = ancestorMap.get(ancestorId)
        if (ancestorGroup) {
          ancestorGroup.roles.push(role)
        }
      }
    })

    return Array.from(ancestorMap.values()).map((ancestor) => ({
      id: String(ancestor.id),
      name: ancestor.name,
      expanded: true,
      subItems: ancestor.roles.map((role) => ({
        subId: role.uuid,
        subName: role.name ?? '',
      })),
    }))
  }, [jobCatalogRoles])

  const selectedRoleEntity = useMemo<EntitySelectEntity | undefined>(() => {
    if (!internalRoleUuid) return undefined

    for (const entity of roleEntities) {
      const matchingSubItem = entity.subItems?.find((subItem) => subItem.subId === internalRoleUuid)
      if (matchingSubItem) {
        return {
          ...entity,
          subItems: [matchingSubItem],
        }
      }
    }
    return undefined
  }, [roleEntities, internalRoleUuid])

  const levelOptions = useMemo(
    () =>
      levels.map((level) => ({
        label: level.name ?? '',
        value: level.uuid,
      })),
    [levels]
  )

  useEffect(() => {
    onLevelSelectRef.current = onLevelSelect
  })

  useEffect(() => {
    setInternalRoleUuid((current) => (selectedRoleUuid !== current ? selectedRoleUuid : current))
    setInternalLevelUuid((current) =>
      selectedRoleLevelUuid !== current ? selectedRoleLevelUuid : current
    )
  }, [selectedRoleUuid, selectedRoleLevelUuid])

  useEffect(() => {
    if (!internalRoleUuid || levels.length === 0 || !internalLevelUuid) {
      return
    }

    const levelExists = levels.some((level) => level.uuid === internalLevelUuid)
    if (!levelExists) {
      setInternalLevelUuid(undefined)
      onLevelSelectRef.current(internalRoleUuid, undefined)
    }
  }, [levels, internalLevelUuid, internalRoleUuid])

  const handleRoleSelect = useCallback((selectedEntity: EntitySelectEntity | null) => {
    if (!selectedEntity?.subItems?.[0]) {
      return
    }

    const roleId = String(selectedEntity.subItems[0].subId)
    setInternalRoleUuid(roleId)
    setInternalLevelUuid(undefined)
    onLevelSelectRef.current(roleId, undefined)
  }, [])

  const handleLevelSelect = useCallback(
    (selectedLevel: string) => {
      setInternalLevelUuid(selectedLevel)
      onLevelSelectRef.current(internalRoleUuid, selectedLevel)
    },
    [internalRoleUuid]
  )

  return (
    <Grid variant="fixed">
      <Box gridColSpan={{ xxs: 1, xs: 6 }}>
        <EntitySelect
          width={360}
          label={i18n.t('job_catalog.job_catalog_tree_selector.role.label')}
          searchPlaceholder={i18n.t(
            'job_catalog.job_catalog_tree_selector.role.search_placeholder'
          )}
          notFoundTitle={i18n.t('job_catalog.job_catalog_tree_selector.role.not_found_title')}
          notFoundSubtitle={i18n.t('job_catalog.job_catalog_tree_selector.role.not_found_subtitle')}
          entities={roleEntities}
          groups={[]}
          selectedGroup="all"
          onSelect={handleRoleSelect}
          onItemExpandedChange={() => {}}
          onGroupChange={() => {}}
          selectedEntities={selectedRoleEntity ? [selectedRoleEntity] : []}
          selectedItemsCopy={i18n.t('job_catalog.job_catalog_tree_selector.role.trigger_selected')}
          placeholder={i18n.t('job_catalog.job_catalog_tree_selector.role.trigger_placeholder')}
          singleSelector
          hiddenAvatar
          size="md"
        />
      </Box>
      <Box gridColSpan={{ xxs: 1, xs: 6 }}>
        <Select
          label={i18n.t('job_catalog.job_catalog_tree_selector.level.label')}
          name="job_role_level"
          placeholder={i18n.t('job_catalog.job_catalog_tree_selector.level.placeholder')}
          loading={levelsLoading}
          onChange={handleLevelSelect}
          size="md"
          value={internalLevelUuid}
          options={levelOptions}
          disabled={!internalRoleUuid || levels.length === 0}
        />
      </Box>
    </Grid>
  )
}
